import { collection, db, getDocsFromServer } from "./dbMariaFirestoreCompat";

export interface CachedRecord {
  id: string;
  [key: string]: any;
}

interface CacheEntry {
  updatedAt: number;
  records: CachedRecord[];
}

const memoryCache = new Map<string, CacheEntry>();

const storageKey = (collectionName: string) => `dbmaria_cache_${collectionName}`;

const readStorage = (collectionName: string): CacheEntry | null => {
  try {
    const raw = localStorage.getItem(storageKey(collectionName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed || !Array.isArray(parsed.records) || typeof parsed.updatedAt !== "number") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeStorage = (collectionName: string, entry: CacheEntry) => {
  try {
    localStorage.setItem(storageKey(collectionName), JSON.stringify(entry));
  } catch {
    // ignore localStorage errors (quota/private mode)
  }
};

const writeCache = (collectionName: string, records: CachedRecord[], updatedAt: number = Date.now()) => {
  const entry: CacheEntry = { records, updatedAt };
  memoryCache.set(collectionName, entry);
  writeStorage(collectionName, entry);
};

const readCacheEntry = (collectionName: string): CacheEntry | null => {
  const inMemory = memoryCache.get(collectionName);
  if (inMemory) return inMemory;
  const fromStorage = readStorage(collectionName);
  if (fromStorage) {
    memoryCache.set(collectionName, fromStorage);
    return fromStorage;
  }
  return null;
};

const fetchFromServer = async (collectionName: string): Promise<CachedRecord[]> => {
  const snapshot = await getDocsFromServer(collection(db, collectionName));
  return snapshot.docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data()
  }));
};

export const getCachedCollection = async (
  collectionName: string,
  options?: { maxAgeMs?: number; forceRefresh?: boolean }
): Promise<CachedRecord[]> => {
  const maxAgeMs = options?.maxAgeMs ?? 5 * 60 * 1000;
  const forceRefresh = options?.forceRefresh ?? false;
  const now = Date.now();

  const current = readCacheEntry(collectionName);
  const isFresh = !!current && now - current.updatedAt <= maxAgeMs;
  if (!forceRefresh && current && isFresh) {
    return current.records;
  }

  const records = await fetchFromServer(collectionName);
  writeCache(collectionName, records, now);
  return records;
};

export const refreshCachedCollection = async (collectionName: string): Promise<CachedRecord[]> => {
  const records = await fetchFromServer(collectionName);
  writeCache(collectionName, records, Date.now());
  return records;
};

export const setCachedCollection = (collectionName: string, records: CachedRecord[]) => {
  writeCache(collectionName, records, Date.now());
};

export const upsertCachedRecord = (collectionName: string, record: CachedRecord, previousId?: string) => {
  const current = readCacheEntry(collectionName)?.records ?? [];
  const withoutPrevious = previousId
    ? current.filter(r => r.id !== previousId)
    : [...current];

  const existingIndex = withoutPrevious.findIndex(r => r.id === record.id);
  if (existingIndex >= 0) {
    withoutPrevious[existingIndex] = { ...withoutPrevious[existingIndex], ...record };
    writeCache(collectionName, withoutPrevious, Date.now());
    return;
  }

  writeCache(collectionName, [...withoutPrevious, record], Date.now());
};

export const removeCachedRecord = (collectionName: string, recordId: string) => {
  const current = readCacheEntry(collectionName)?.records ?? [];
  writeCache(collectionName, current.filter(r => r.id !== recordId), Date.now());
};
